"use client";

// RUM beacon: Core Web Vitals + page views → POST /api/telemetry.
// Mounted once in the root layout, renders nothing. Same invariants as the
// error reporter: never throws, never awaits in user paths, never sends
// identity (the server fills actor fields from the session cookie).
//
// Page views land in wf_page_views (step-level funnel — which page couples
// stall on); vitals land in wf_web_vitals (LCP/INP/CLS etc. — real-user
// performance, previously not collected at all).

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useReportWebVitals } from "next/web-vitals";
import { getSessionId } from "@/lib/session-id";

const ENDPOINT = "/api/telemetry";
const VITALS = new Set(["LCP", "INP", "CLS", "FCP", "TTFB"]);

function post(body: Record<string, unknown>): void {
  try {
    void fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
      credentials: "same-origin",
    }).catch(() => {});
  } catch {
    // telemetry must never throw
  }
}

export function TelemetryBeacon() {
  const pathname = usePathname();
  const prevPath = useRef<string | null>(null);

  useReportWebVitals((metric) => {
    // Next also reports its own custom metrics (hydration etc.) through this
    // hook — only the standard vitals go to the table.
    if (!VITALS.has(metric.name)) return;
    if (typeof metric.value !== "number" || !Number.isFinite(metric.value)) return;
    post({
      type: "web-vital",
      metric: metric.name,
      metric_id: metric.id,
      value: metric.value,
      rating: (metric as { rating?: string }).rating,
      route: typeof location !== "undefined" ? location.pathname : undefined,
      session_id: getSessionId() ?? undefined,
    });
  });

  useEffect(() => {
    if (!pathname || prevPath.current === pathname) return;
    post({
      type: "page_view",
      route: pathname,
      referrer_route: prevPath.current ?? undefined,
      session_id: getSessionId() ?? undefined,
    });
    prevPath.current = pathname;
  }, [pathname]);

  return null;
}
